import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
  type MessageCategory,
  type NotificationPreferences,
  type SendableChannel,
} from '@gwc/shared';
import { notificationsApi } from '../api/notifications';

const KEY = ['notification-preferences'];

export function useNotificationPreferences() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: KEY,
    queryFn: () => notificationsApi.getPreferences(),
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      notificationsApi.updatePreferences(patch),
    // Optimistic: a toggle that lags behind the network feels broken.
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<NotificationPreferences>(KEY);
      if (prev) {
        const next: NotificationPreferences = { ...prev };
        for (const [k, v] of Object.entries(patch) as [MessageCategory, { push?: boolean; email?: boolean }][]) {
          if (v) next[k] = { ...prev[k], ...v };
        }
        qc.setQueryData(KEY, next);
      }
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(KEY, ctx.prev);
    },
    onSuccess: (prefs) => qc.setQueryData(KEY, prefs),
  });

  const preferences = query.data ?? DEFAULT_NOTIFICATION_PREFERENCES;

  /** Flip one channel of one category — e.g. "email me about dues". */
  function toggle(category: MessageCategory, channel: SendableChannel) {
    mutation.mutate({
      [category]: { [channel]: !preferences[category][channel] },
    } as Partial<NotificationPreferences>);
  }

  return {
    preferences: query.data,
    loading: query.isLoading,
    toggle,
    saving: mutation.isPending,
  };
}

export { MESSAGE_CATEGORIES, MESSAGE_CATEGORY_LABELS };
